import { useAtom } from "jotai";
import { useUpdateAtom } from "jotai/utils";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useSendAnalyticsEvent } from "../analytics/events";
import { logout, useAuth } from "../auth";
import { StyledLinkButton } from "../components/Button";
import Icon from "../components/Icon";
import { Loading } from "../components/Loading";
import { Menu, MenuItem } from "../components/Menu";
import { BOT_NAME } from "../consts";
import {
  bugReportOnAtom,
  consoleOnAtom,
  currentUserAtom,
  isInAuthFlowAtom,
  sideNavOnAtom,
} from "../jotai/atoms";
import { styled } from "../stitches.config";
import { generatenewSpaceId } from "../utils/generatenewSpaceId";
import { spaceDisplayName } from "../utils/spaceDisplayName";
import useKeyAction from "../utils/useKeyAction";
import { ButtonNew } from "./auth/AuthDialogComponents";
import { useSessionHistory } from "./BigSpaceList";

export function SideNav() {
  const router = useRouter();
  const [navOn, setNavOn] = useAtom(sideNavOnAtom);
  const [currentUser] = useAtom(currentUserAtom);
  const setIsInAuthFlow = useUpdateAtom(isInAuthFlowAtom);

  useKeyAction({ key: "i", ctrlKey: true }, () => {
    setNavOn(!navOn);
  });

  useEffect(() => {
    if (currentUser?.name === BOT_NAME) {
      setNavOn(false);
    }
  }, [currentUser, setNavOn]);

  function onCreateClick() {
    if (currentUser?.anonymous) {
      setIsInAuthFlow(true);
      return;
    }

    const newSpaceId = generatenewSpaceId();
    const location = "/space/" + newSpaceId;
    router.push(location);
  }

  return (
    <Container on={navOn}>
      <Profile />
      <CreateButton onClick={onCreateClick}>
        <Icon icon="Plus" />
        {currentUser?.anonymous ? "Claim your own space" : "Create space"}
      </CreateButton>
      <History />
      <BottomMenu />
    </Container>
  );
}

function History() {
  const [currentUser] = useAtom(currentUserAtom);
  const res = useSessionHistory();

  if (currentUser?.anonymous) return null;

  let content = null;

  switch (res.status) {
    case "fetching": {
      content = (
        <Image width={60} height={60} src="/assets/loading.gif" alt="loading" />
      );
      break;
    }
    case "error": {
      content = <div>error</div>;
      break;
    }
    case "fetched": {
      content = (
        <SpaceList>
          {res.sessions?.map((s) => {
            const name = spaceDisplayName(s.spaceId, s.spaceName);
            return (
              <SpaceItem key={s.spaceId}>
                <Link href={`/space/${s.spaceId}`} passHref>
                  <StyledLinkButton>{name}</StyledLinkButton>
                </Link>
              </SpaceItem>
            );
          })}
        </SpaceList>
      );
    }
  }

  return (
    <div>
      <SectionHeader>RECENTLY VISITED</SectionHeader>
      {content}
    </div>
  );
}

function Profile() {
  const { user } = useAuth();
  const [currentUser] = useAtom(currentUserAtom);
  const [, setIsInAuthFlow] = useAtom(isInAuthFlowAtom);

  if (!currentUser) return null;

  const title = user ? user.playerId : currentUser.name;
  function onJoinClick() {
    setIsInAuthFlow(true);
  }

  return (
    <ProfileContainer>
      <Menu
        icon="Logo"
        ariaLabel="Menu"
        label={<ProfileMenuLabel>{title}</ProfileMenuLabel>}
      >
        <MenuItem
          onSelect={() => {
            logout();
          }}
        >
          <Icon icon="Leave" />
          Log out
        </MenuItem>
      </Menu>
      {currentUser.anonymous ? (
        <ButtonNew color="primary" onClick={onJoinClick}>
          Join Sprout
        </ButtonNew>
      ) : null}
    </ProfileContainer>
  );
}

function BottomMenu() {
  const [consoleOn, setConsoleOn] = useAtom(consoleOnAtom);
  const [bugReportOn, setBugReportOn] = useAtom(bugReportOnAtom);
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  function onFeedbackClick() {
    setBugReportOn(!bugReportOn);
  }

  function onConsoleClick() {
    if (!consoleOn) {
      sendAnalyticsEvent({ name: "experimentsPanelOpened" });
    }
    setConsoleOn(!consoleOn);
  }

  return (
    <BottomSection>
      <StyledLinkButton
        as="a"
        href="https://discord.gg/WWdc59WayG"
        target="_blank"
        rel="noreferrer"
      >
        Join Discord
      </StyledLinkButton>
      <StyledLinkButton onClick={onFeedbackClick}>
        Write feedback!
      </StyledLinkButton>
      <StyledLinkButton onClick={onConsoleClick}>
        Experimental features
      </StyledLinkButton>
    </BottomSection>
  );
}

const Container = styled("div", {
  position: "fixed",
  top: "0",
  left: "0",
  width: "$sideNavWidth",
  height: "100vh",
  zIndex: "$sideNav",
  background: "$menuBeige",
  fontSize: 14,
  padding: "0 10px",
  overflowY: "auto",
  transform: "translateX(0)",
  transition: "transform 100ms ease-in-out",
  cursor: "default",
  variants: {
    on: {
      true: {
        transform: "translateX(0)",
      },
      false: {
        transform: "translateX(calc(-1 * $sizes$sideNavWidth))",
      },
    },
  },
});

const CreateButton = styled(StyledLinkButton, {
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  margin: "10px 0 20px -5px",
  color: "$paper10",
  lineHeight: "20px",
  fontSize: 14,
  fontWeight: 500,

  "& svg": {
    marginRight: "5px",
  },

  "&:hover": {
    color: "$paper10",
  },
});

const SectionHeader = styled("div", {
  color: "$paper60",
  fontSize: "10px",
  fontWeight: 500,
  marginLeft: "5px",
  marginBottom: "10px",
});

const SpaceList = styled("ul", {
  padding: 0,
});

const SpaceItem = styled("li", {
  padding: 0,
  [`& ${StyledLinkButton}`]: {
    display: "block",
    fontSize: "14px",
    fontWeight: 500,
    color: "$paper10",
    textDecoration: "none",
    borderRadius: "$1",
    margin: "0",
    padding: "10px 5px",
  },
});

const ProfileContainer = styled("div", {
  paddingLeft: "10px",
  borderBottom: "1px solid $paper90",
  padding: "10px 0",
  position: "sticky",
  background: "$menuBeige",
  top: 0,

  [`& ${ButtonNew}`]: {
    display: "block",
    width: "100%",
    marginTop: "10px",
  },
});

const ProfileMenuLabel = styled("div", {
  fontWeight: 500,
  fontSize: 14,
  marginLeft: 5,
});

const BottomSection = styled("div", {
  position: "sticky",
  background: "$menuBeige",
  bottom: 0,
  marginTop: 20,
  marginBottom: 10,
  [`& ${StyledLinkButton}`]: {
    display: "block",
    padding: "10px 5px",
    margin: "0",
    color: "$paper10_50",
    fontSize: 14,
    fontWeight: 500,
    textDecoration: "none",

    "&:hover": {
      color: "$paper10",
    },
  },
});
